import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  fetchAllRequirements,
  fetchAllRequirementTags,
  fetchRequirementById,
  fetchRequirementsByIds,
  fetchRequirementTagsById,
  fetchRequirementtypes,
  fetchRequirementLinks,
  fetchTestCasesByRequirementId,
  fetchRequirementActivity,
  fetchRequirementAudit,
  fetchRequirementDashboard,
} from "./requirements.api";

interface QueryParams {
  versionId: number;
  projectId?: number;
  folderId?: number;
  testCaseFolderId?: number;
  page?: number;
  size?: number;
  query?: string;
  search?: string;
  sort?: string;
  /** When true, a selected folder also returns requirements from nested folders. */
  recursive?: boolean;
}

export const useAllRequirements = (params: QueryParams) =>
  useQuery({
    queryKey: ["allRequirements", params],
    queryFn: () => fetchAllRequirements(params),
    enabled: !!params.versionId,
  });

export const useAllRequirementsInfinite = (
  params: Omit<QueryParams, "page">,
  options?: { enabled?: boolean },
) =>
  useInfiniteQuery({
    queryKey: ["allRequirements", params],
    // use pageParam provided by react-query to fetch correct page
    queryFn: ({ pageParam }) =>
      fetchAllRequirements({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!params.versionId && (options?.enabled ?? true),
  });

export const useAllRequirementTypes = (params: QueryParams) =>
  useQuery({
    queryKey: ["allRequirementTypes", params.projectId ?? params.versionId],
    queryFn: () => fetchRequirementtypes(params),
    enabled: !!params.versionId || !!params.projectId,
  });
export const useRequirementById = (
  id: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["requirementById", id],
    queryFn: () => fetchRequirementById(id),
    enabled: !!id && (options?.enabled ?? true), // avoids calling with undefined
  });
};

/** Batch-fetch many requirements in one request — returns a Map keyed by id. */
export const useRequirementsByIds = (
  ids: number[],
  versionId?: number,
  options?: { enabled?: boolean },
) => {
  const sortedIds = [...new Set(ids.filter((n) => !!n))].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["requirementsByIds", sortedIds, versionId],
    queryFn: async () => {
      const res = await fetchRequirementsByIds(sortedIds, versionId);
      const content: any[] = (res as any)?.data?.content ?? [];
      const byId = new Map<number, any>();
      for (const r of content) byId.set(Number(r.id), r);
      return byId;
    },
    enabled: sortedIds.length > 0 && (options?.enabled ?? true),
  });
};

export const useAllRequirementTags = () =>
  useQuery({
    queryKey: ["allRequirementTags"],
    queryFn: () => fetchAllRequirementTags(),
  });

export const useRequirementTagsById = (
  requirementId: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["requirementTagsById", requirementId],
    queryFn: () => fetchRequirementTagsById(requirementId),
    enabled: !!requirementId && (options?.enabled ?? true),
  });

export const useRequirementLinks = (
  requirementId: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["requirementLinks", requirementId],
    queryFn: async () => {
      const res = await fetchRequirementLinks(requirementId);
      return (res?.data ?? res ?? []) as number[];
    },
    enabled: !!requirementId && (options?.enabled ?? true),
  });

export const useTestCasesByRequirementId = (
  requirementId: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["testCasesByRequirement", requirementId],
    queryFn: async () => {
      const res = await fetchTestCasesByRequirementId(requirementId);
      const data: any = res?.data ?? res ?? {};
      return (data.content ?? []) as any[];
    },
    enabled: !!requirementId && (options?.enabled ?? true),
  });

// ── Group B: activity timeline, audit history, dashboard ───────────────────

export const useRequirementActivity = (
  requirementId: number,
  options?: { enabled?: boolean; limit?: number },
) =>
  useQuery({
    queryKey: ["requirementActivity", requirementId, options?.limit ?? 100],
    queryFn: async () => {
      const res = await fetchRequirementActivity(
        requirementId,
        options?.limit ?? 100,
      );
      const data: any = (res as any)?.data ?? res ?? {};
      return (data.items ?? []) as import("./requirements.api").ActivityItem[];
    },
    enabled: !!requirementId && (options?.enabled ?? true),
  });

export const useRequirementAudit = (
  requirementId: number,
  params?: { page?: number; size?: number },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: [
      "requirementAudit",
      requirementId,
      params?.page ?? 0,
      params?.size ?? 50,
    ],
    queryFn: async () => {
      const res = await fetchRequirementAudit(requirementId, params);
      return (res as any)?.data ?? res;
    },
    enabled: !!requirementId && (options?.enabled ?? true),
  });

export const useRequirementDashboard = (
  versionId?: number,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["requirementDashboard", versionId],
    queryFn: async () => {
      const res = await fetchRequirementDashboard(versionId as number);
      return ((res as any)?.data ??
        res) as import("./requirements.api").RequirementDashboard;
    },
    enabled: !!versionId && (options?.enabled ?? true),
  });
